import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, QrCode, Trash2 } from "lucide-react";
import { BatchQrDialog } from "./BatchQr";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import axios, { API, authHeaders, formatError } from "../../lib/api";

const today = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const blank = (slug) => ({ batch_no: "", test_date: today(), product_slug: slug || "", moisture_pct: "", ash_pct: "", gcv_kcal: "", density_kg_m3: "", fines_pct: "", lab: "", result: "pass", notes: "" });
const NUMS = [["moisture_pct", "Moisture %", true], ["ash_pct", "Ash %", true], ["gcv_kcal", "GCV kcal/kg", true], ["density_kg_m3", "Bulk density kg/m³", false], ["fines_pct", "Fines %", false]];
const num = (v) => (v === "" || v == null ? null : Number(v));

export const ReportsAdmin = ({ products, onAuthFail }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(blank(products[0]?.slug));
  const [editId, setEditId] = useState(null);
  const [qr, setQr] = useState(null);
  const names = Object.fromEntries(products.map((p) => [p.slug, p.name]));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reports`, { headers: authHeaders() });
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(formatError(err));
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!f.product_slug && products[0]) setF((x) => ({ ...x, product_slug: products[0].slug }));
  }, [products, f.product_slug]);

  const reset = () => { setF(blank(products[0]?.slug)); setEditId(null); };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, moisture_pct: num(f.moisture_pct), ash_pct: num(f.ash_pct), gcv_kcal: num(f.gcv_kcal) == null ? null : Math.round(num(f.gcv_kcal)), density_kg_m3: num(f.density_kg_m3), fines_pct: num(f.fines_pct) };
    try {
      if (editId) await axios.put(`${API}/admin/reports/${editId}`, body, { headers: authHeaders() });
      else await axios.post(`${API}/admin/reports`, body, { headers: authHeaders() });
      toast.success(`Batch ${f.batch_no} ${editId ? "updated" : "added"}`);
      reset();
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const edit = (r) => {
    setEditId(r.id);
    setF(Object.fromEntries(Object.entries(blank()).map(([k, d]) => [k, r[k] ?? d])));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete test report for batch ${r.batch_no}?`)) return;
    try {
      await axios.delete(`${API}/admin/reports/${r.id}`, { headers: authHeaders() });
      toast.success("Report deleted");
      if (editId === r.id) reset();
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sel = "h-11 w-full rounded-md border border-input bg-transparent px-3 text-sm";

  return (
    <div className="grid gap-5">
      <form data-testid="admin-report-form" onSubmit={save} className="rounded-[24px] border hairline bg-[var(--forest-2)] p-6 sm:p-8">
        <p className="font-display text-2xl font-semibold">{editId ? `Edit batch ${f.batch_no}` : "Add test report"}</p>
        <p className="text-sm text-[var(--sage)]">Reports from the last 12 months show on the public Test reports page.</p>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5"><Label htmlFor="rp-batch">Batch number</Label><Input id="rp-batch" data-testid="admin-report-batch" required maxLength={40} value={f.batch_no} onChange={set("batch_no")} className="h-11" /></div>
          <div className="space-y-1.5"><Label htmlFor="rp-date">Test date</Label><Input id="rp-date" data-testid="admin-report-date" type="date" max={today()} required value={f.test_date} onChange={set("test_date")} className="h-11" /></div>
          <div className="space-y-1.5"><Label htmlFor="rp-prod">Product</Label>
            <select id="rp-prod" data-testid="admin-report-product" value={f.product_slug} onChange={set("product_slug")} className={sel}>{products.map((p) => <option key={p.slug} value={p.slug} className="bg-[#0c241e]">{p.name}</option>)}</select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="rp-res">Result</Label>
            <select id="rp-res" data-testid="admin-report-result" value={f.result} onChange={set("result")} className={sel}><option value="pass" className="bg-[#0c241e]">Pass</option><option value="fail" className="bg-[#0c241e]">Fail</option></select>
          </div>
          {NUMS.map(([k, label, req]) => (
            <div key={k} className="space-y-1.5"><Label htmlFor={`rp-${k}`}>{label}{req ? "" : " (optional)"}</Label><Input id={`rp-${k}`} data-testid={`admin-report-${k}`} type="number" step="0.01" min="0" required={req} value={f[k] ?? ""} onChange={set(k)} className="h-11" /></div>
          ))}
          <div className="space-y-1.5"><Label htmlFor="rp-lab">Lab (optional)</Label><Input id="rp-lab" data-testid="admin-report-lab" maxLength={120} value={f.lab} onChange={set("lab")} className="h-11" /></div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-4"><Label htmlFor="rp-notes">Internal notes (optional, not shown publicly)</Label><Input id="rp-notes" data-testid="admin-report-notes" maxLength={300} value={f.notes} onChange={set("notes")} className="h-11" /></div>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="admin-report-save" className="h-12 flex-1 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold">{editId ? "Save changes" : "Add report"}</button>
          {editId && <button type="button" data-testid="admin-report-cancel" onClick={reset} className="h-12 px-6 rounded-full border hairline">Cancel</button>}
        </div>
      </form>
      <div data-testid="admin-reports-list" className="rounded-[24px] border hairline bg-[var(--forest-2)] p-4 sm:p-6">
        {list.length === 0 && <p data-testid="admin-reports-empty" className="text-sm text-[var(--sage)] p-2">No test reports yet.</p>}
        {list.map((r) => (
          <div key={r.id} data-testid={`admin-report-${r.batch_no}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b hairline last:border-0 py-3 text-sm">
            <span className="font-mono text-[var(--husk)] w-28">{r.batch_no}</span>
            <span className="w-24">{r.test_date}</span>
            <span className="w-28 text-[var(--sage)]">{names[r.product_slug] || r.product_slug}</span>
            <span className="flex-1 min-w-[180px] text-[var(--sage)]">M {r.moisture_pct}% · A {r.ash_pct}% · {r.gcv_kcal} kcal · {r.result === "fail" ? "Fail" : "Pass"}</span>
            <button data-testid={`admin-report-qr-${r.batch_no}`} onClick={() => setQr(r)} aria-label="Batch QR code" className="grid h-9 w-9 place-items-center rounded-full border hairline hover:bg-white/5"><QrCode className="h-4 w-4" /></button>
            <button data-testid={`admin-report-edit-${r.batch_no}`} onClick={() => edit(r)} aria-label="Edit report" className="grid h-9 w-9 place-items-center rounded-full border hairline hover:bg-white/5"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`admin-report-delete-${r.batch_no}`} onClick={() => remove(r)} aria-label="Delete report" className="grid h-9 w-9 place-items-center rounded-full border hairline hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <BatchQrDialog report={qr} productName={qr ? names[qr.product_slug] || qr.product_slug : ""} onClose={() => setQr(null)} />
    </div>
  );
};
